import React, { useState } from 'react';
import { PersonFinal } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Upload, Loader2, CheckCircle2, AlertCircle, FileText } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function DepartmentUploader() {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentAction, setCurrentAction] = useState('');
  const [results, setResults] = useState(null);
  const { toast } = useToast();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResults(null);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please upload a CSV file',
        variant: 'destructive'
      });
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    const personIdIndex = headers.indexOf('person_id');
    const departmentIndex = headers.indexOf('department');
    
    if (personIdIndex === -1 || departmentIndex === -1) {
      throw new Error('CSV must have person_id and department columns');
    }

    const records = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length > Math.max(personIdIndex, departmentIndex)) {
        records.push({
          person_id: values[personIdIndex],
          department: values[departmentIndex]
        });
      }
    }

    return records;
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const updatePersonWithRetry = async (personId, department, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await PersonFinal.update(personId, { department });
        return { status: 'updated' };
      } catch (error) {
        const isRateLimit = error.message?.includes('rate limit') || 
                           error.message?.includes('429') ||
                           error.status === 429;
        
        if (isRateLimit && attempt < maxRetries - 1) {
          // Exponential backoff: 2s, 4s, 8s
          const waitTime = Math.pow(2, attempt + 1) * 1000;
          console.log(`Rate limit hit, waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
          await sleep(waitTime);
          continue;
        }
        
        throw error;
      }
    }
  };

  const processBatch = async (records) => {
    const batchSize = 3; // Reduced from 10 to 3
    const delayBetweenBatches = 2000; // Increased from 1s to 2s
    
    let updated = 0;
    let notFound = 0;
    const errors = [];

    // First, load all people to create a lookup map
    setCurrentAction('Loading all personas...');
    const allPeople = await PersonFinal.list();
    const peopleMap = new Map();
    allPeople.forEach(p => {
      if (p.person_id) peopleMap.set(p.person_id, p);
    });

    for (let i = 0; i < records.length; i += batchSize) {
      const batch = records.slice(i, i + batchSize);
      
      setCurrentAction(`Processing ${i + 1}-${Math.min(i + batchSize, records.length)} of ${records.length}...`);
      setProgress(Math.round((i / records.length) * 100));

      // Process batch sequentially (not in parallel) to avoid rate limits
      for (const record of batch) {
        try {
          const person = peopleMap.get(record.person_id);
          
          if (!person) {
            notFound++;
            continue;
          }

          await updatePersonWithRetry(person.id, record.department);
          updated++;
          
          // Small delay between individual updates within batch
          await sleep(300);
          
        } catch (error) {
          console.error(`Error updating person ${record.person_id}:`, error);
          errors.push(`${record.person_id}: ${error.message || 'Unknown error'}`);
        }
      }

      // Longer delay between batches
      if (i + batchSize < records.length) {
        await sleep(delayBetweenBatches);
      }
    }

    setProgress(100);
    setCurrentAction('Complete!');

    return { updated, notFound, errors };
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file first',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);
    setResults(null);
    setProgress(0);
    setCurrentAction('Reading file...');

    try {
      const text = await file.text();
      const records = parseCSV(text);

      if (records.length === 0) {
        throw new Error('No valid records found in CSV');
      }

      setCurrentAction(`Found ${records.length} records. Starting batch update...`);
      
      const result = await processBatch(records);
      
      setResults({
        total: records.length,
        ...result
      });

      toast({
        title: 'Upload complete',
        description: `Updated ${result.updated} departments, ${result.notFound} not found`,
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Upload failed',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setCurrentAction('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Department Uploader</CardTitle>
        <CardDescription>
          Bulk update department fields for personas (Super Admin Only)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Upload CSV File</label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={isProcessing}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
              />
              {file && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span>{file.name}</span>
                </div>
              )}
            </div>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} className="w-full" />
              <p className="text-sm text-gray-600 text-center">{currentAction}</p>
            </div>
          )}

          <Button
            onClick={handleUpload}
            disabled={!file || isProcessing}
            className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload & Process
              </>
            )}
          </Button>
        </div>

        {results && (
          <div className={`border rounded-lg p-4 ${
            results.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-start gap-2">
              {results.errors.length > 0 ? (
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h4 className={`font-semibold mb-2 ${
                  results.errors.length > 0 ? 'text-yellow-900' : 'text-blue-900'
                }`}>
                  Upload Results
                </h4>
                <div className={`text-sm space-y-1 ${
                  results.errors.length > 0 ? 'text-yellow-800' : 'text-blue-800'
                }`}>
                  <p>Total records: {results.total}</p>
                  <p>Updated: {results.updated}</p>
                  <p>Not found: {results.notFound}</p>
                  {results.errors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-yellow-200">
                      <p className="font-semibold text-red-600">Errors ({results.errors.length}):</p>
                      <ul className="list-disc pl-5 mt-1">
                        {results.errors.slice(0, 10).map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                        {results.errors.length > 10 && (
                          <li>...and {results.errors.length - 10} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 font-semibold mb-2">CSV Format Requirements:</p>
          <ul className="text-sm text-blue-800 space-y-1 list-disc pl-5">
            <li>Must have columns: <code>person_id</code>, <code>department</code></li>
            <li>First row should be headers</li>
            <li>Can handle ~1000 records safely with rate limit protection</li>
            <li>Processing 1000 records takes approximately 5-8 minutes (slow but reliable)</li>
            <li>Includes automatic retry with exponential backoff on rate limits</li>
          </ul>
          <p className="text-sm text-blue-800 mt-2">
            <strong>Example:</strong>
          </p>
          <pre className="text-xs bg-white p-2 rounded mt-1 overflow-x-auto">
person_id,department
john_smith_123,IT
jane_doe_456,Finance
bob_jones_789,Executive
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}